import type { HydraItem } from "../Api/Type/hydra.interface";
import { StorageSerializers, useStorage } from "@vueuse/core";
import { EventSourcePolyfill } from "event-source-polyfill";
import { getCurrentInstance, onBeforeUnmount, readonly, ref, watch } from "vue";

export const disableMercure = useStorage<boolean>("disableMercure", false, undefined, {
  serializer: StorageSerializers.boolean,
});

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

// Diagnostics only: total EventSources currently held by all useMercure instances.
let liveSocketCount = 0;

interface MercureInit<T> {
  topic?: string | null
  onAddOrUpdate?: (message: HydraItem<T>) => any
  onDelete?: (message: HydraItem<T>) => any
  hubUrlGetter: () => string | null
  mercureTokenGetter: () => string | null
  refreshMercureToken: () => Promise<void>
  closeOnUnmount?: boolean
}

export function useMercure<T>({
  hubUrlGetter,
  mercureTokenGetter,
  refreshMercureToken,
  topic = null,
  onAddOrUpdate,
  onDelete,
  closeOnUnmount = true,
}: MercureInit<T>) {
  let eventSource: EventSourcePolyfill | null = null;
  let lastEvent: string | null = null;
  let activeTopic: string | null = topic;
  // Bumped on every connect/close so in-flight async work cannot open a second socket.
  let connectionEpoch = 0;
  // True after a 401 refresh is claimed for the current connection attempt; cleared on open.
  let authRetryUsed = false;
  let reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let connectChain: Promise<void> = Promise.resolve();
  // True only while the hub stream is open; false while connecting, retrying, after an error or a close.
  const isOpen = ref(false);
  // Counts the errors of the stream (transient or terminal), so a caller can tell an open after a failure.
  const failures = ref(0);

  function isConnected() {
    // eventSource is nulled on dispose/close, so non-null means connecting or open.
    return eventSource !== null;
  }

  function clearReconnectTimer() {
    if (reconnectTimer === null) {
      return;
    }
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  function disposeEventSource() {
    if (!eventSource) {
      return;
    }

    const es = eventSource;
    eventSource = null;
    isOpen.value = false;
    liveSocketCount -= 1;
    es.onerror = null;
    es.onmessage = null;
    es.onopen = null;
    es.close();
  }

  async function openConnection(epoch: number) {
    if (epoch !== connectionEpoch || disableMercure.value) {
      return;
    }

    if (!mercureTokenGetter()) {
      console.debug("[Mercure]: no token, refreshing...");
      await refreshMercureToken();
    } else {
      console.debug("[Mercure]: token exists.");
    }

    if (epoch !== connectionEpoch || disableMercure.value) {
      return;
    }

    const hubUrl = hubUrlGetter();
    if (!hubUrl) {
      console.warn("Mercure: hubUrl is not set or empty. Not connecting.");
      return;
    }

    const topicToUse = activeTopic;
    if (!topicToUse) {
      return;
    }

    const url = new URL(`${hubUrl}/.well-known/mercure`, window.origin);
    url.searchParams.append("topic", `/api${topicToUse}`);

    if (lastEvent) {
      url.searchParams.set("lastEventId", lastEvent);
    }

    const config = {
      headers: {
        Authorization: `Bearer ${mercureTokenGetter()}`,
      },
    };

    disposeEventSource();

    if (epoch !== connectionEpoch || disableMercure.value) {
      return;
    }

    const es = new EventSourcePolyfill(url.toString(), config);
    if (epoch !== connectionEpoch || disableMercure.value) {
      es.close();
      return;
    }

    eventSource = es;
    liveSocketCount += 1;
    es.onerror = e => onError(e, epoch);
    es.onmessage = onMessage;
    es.onopen = () => {
      if (epoch !== connectionEpoch) {
        return;
      }
      isOpen.value = true;
      console.debug(`Mercure: connected. (live sockets: ${liveSocketCount})`);
      authRetryUsed = false;
      reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
    };
    console.debug(`Mercure: connecting... (live sockets: ${liveSocketCount})`);
  }

  function connect(passedTopic: string) {
    if (disableMercure.value) {
      return Promise.resolve();
    }

    activeTopic = passedTopic;
    clearReconnectTimer();
    const epoch = ++connectionEpoch;

    connectChain = connectChain
      .catch(() => undefined)
      .then(() => openConnection(epoch));

    return connectChain;
  }

  function scheduleReconnect(epoch: number) {
    if (epoch !== connectionEpoch || reconnectTimer !== null || disableMercure.value) {
      return;
    }

    const delay = reconnectDelay;
    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
    console.debug(`Mercure: reconnecting in ${delay}ms.`);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (epoch !== connectionEpoch || !activeTopic) {
        return;
      }
      // Each supervised attempt gets a fresh 401-refresh credit.
      authRetryUsed = false;
      connect(activeTopic);
    }, delay);
  }

  function onError(e: any, epoch: number) {
    console.debug("Mercure: onError()", e, authRetryUsed);
    if (epoch !== connectionEpoch) {
      return;
    }
    // Updates may be missed from here until the next open, also while the polyfill retries by itself.
    isOpen.value = false;
    failures.value += 1;

    // While readyState is CONNECTING the polyfill retries internally (network blips,
    // its own 45s heartbeat timeout, hub write timeouts). Interfering here would
    // race its retry loop and stack connections.
    if (eventSource && eventSource.readyState !== EventSourcePolyfill.CLOSED) {
      return;
    }

    // Terminal close: the polyfill gave up (non-200 response such as 401/502/503, or
    // a non-SSE content-type from a proxy error page). It never retries after this,
    // so supervision is on us.
    disposeEventSource();

    if (e?.status === 401 && !authRetryUsed) {
      // Claim the retry synchronously so concurrent 401s cannot stack reconnects.
      authRetryUsed = true;
      refreshMercureToken()
        .then(() => {
          if (epoch !== connectionEpoch || !activeTopic) {
            return;
          }
          return connect(activeTopic);
        })
        .catch((error) => {
          console.debug("Mercure: token refresh failed", error);
          if (epoch === connectionEpoch) {
            authRetryUsed = false;
            scheduleReconnect(epoch);
          }
        });
      return;
    }

    scheduleReconnect(epoch);
  }

  function close() {
    connectionEpoch += 1;
    authRetryUsed = false;
    reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
    clearReconnectTimer();
    console.debug("Mercure: closing connection.");
    disposeEventSource();
    return true;
  }

  function onMessage(event: any) {
    console.log(event, typeof event);
    lastEvent = event.lastEventId;
    if (!event.data) {
      return;
    }

    const parsed = JSON.parse(event.data);
    const keys = Object.keys(parsed);
    console.log(parsed);

    if (keys.length <= 2) {
      return onDelete && onDelete(parsed);
    }
    return onAddOrUpdate && onAddOrUpdate(parsed);
  }

  // Only register onBeforeUnmount if we're in a component context
  if (closeOnUnmount && getCurrentInstance()) {
    onBeforeUnmount(() => {
      close();
    });
  }

  if (!disableMercure.value) {
    if (topic) {
      connect(topic);
    }
  }
  watch(disableMercure, (newVal) => {
    if (newVal) {
      console.log("Mercur disabled again");
      close();
    }
  });

  return {
    close,
    isConnected,
    isOpen: readonly(isOpen),
    failures: readonly(failures),
    connect,
  };
}
